import { Box, Typography, Tooltip } from '@mui/material';
import {
  timeToDbFormat,
  formatTimeRange,
} from '@features/settings/utils/businessHoursUtils';
import TimelineAvailabilityBlock from './TimelineAvailabilityBlock';
import type { DayHours, DayOverride } from './ScheduleEditor.types';

const DAY_ABBREVS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface TimelineDayRowProps {
  dayIndex: number;
  dayHours: DayHours;
  override?: DayOverride;
  startHour: number;
  endHour: number;
  timezone: string;
  closedLabel: string;
  onClick?: (dayIndex: number) => void;
  hasPendingChanges?: boolean;
}

/** Convert HH:mm:ss string to percent position within the timeline range. */
const timeStrToPercent = (timeStr: string, startHour: number, endHour: number): number => {
  const [hh, mm] = timeStr.split(':').map(Number);
  const fractional = hh + mm / 60;
  return ((fractional - startHour) / (endHour - startHour)) * 100;
};

const TimelineDayRow: React.FC<TimelineDayRowProps> = ({
  dayIndex,
  dayHours,
  override,
  startHour,
  endHour,
  timezone,
  closedLabel,
  onClick,
  hasPendingChanges,
}) => {
  const isAvailable = !dayHours.isClosed && dayHours.timeBlocks.length > 0;
  const effectiveAvailable = override ? override.isAvailable : isAvailable;
  const effectiveClosed = override ? !override.isAvailable : dayHours.isClosed;

  // Build availability blocks as percentages
  const blocks = isAvailable
    ? dayHours.timeBlocks
        .filter((b) => !b.isDefault || !isAvailable)
        .map((block) => {
          const openStr = block.openTime ? timeToDbFormat(block.openTime, timezone) : null;
          const closeStr = block.closedTime ? timeToDbFormat(block.closedTime, timezone) : null;
          if (!openStr || !closeStr) return null;

          const left = timeStrToPercent(openStr, startHour, endHour);
          const right = timeStrToPercent(closeStr, startHour, endHour);
          const tooltipText = formatTimeRange(openStr, closeStr, timezone);

          return { left, width: right - left, tooltip: tooltipText };
        })
        .filter(Boolean) as { left: number; width: number; tooltip: string }[]
    : [];

  // Override block (if override exists and is available)
  const overrideBlock =
    override && override.isAvailable && override.openTime && override.closedTime
      ? {
          left: timeStrToPercent(override.openTime, startHour, endHour),
          width:
            timeStrToPercent(override.closedTime, startHour, endHour) -
            timeStrToPercent(override.openTime, startHour, endHour),
          tooltip: `${override.name}: ${override.formattedTimeRange}`,
        }
      : null;

  return (
    <Box
      onClick={onClick ? () => onClick(dayIndex) : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        height: 36,
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: '6px',
        px: 0.5,
        transition: 'background-color 150ms ease',
        '&:hover': onClick ? {
          bgcolor: 'grey.50',
          '& .timeline-track': { bgcolor: 'grey.200' },
        } : {
          '& .timeline-track': { bgcolor: 'grey.200' },
        },
      }}
    >
      {/* Day label + pending indicator */}
      <Box sx={{ width: 40, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexShrink: 0 }}>
        {hasPendingChanges && (
          <Tooltip title="Unsaved changes" placement="left" arrow>
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: '#f4b020',
                flexShrink: 0,
                boxShadow: '0 0 0 2px #fff9f3',
              }}
            />
          </Tooltip>
        )}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: effectiveClosed ? 'grey.400' : 'grey.700',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: 11,
            userSelect: 'none',
          }}
        >
          {DAY_ABBREVS[dayIndex]}
        </Typography>
      </Box>

      {/* Track */}
      <Box
        className="timeline-track"
        sx={{
          flex: 1,
          position: 'relative',
          height: '100%',
          bgcolor: 'grey.100',
          borderRadius: '4px',
          transition: 'background-color 150ms ease',
          overflow: 'hidden',
        }}
      >
        {/* Hour grid lines */}
        {Array.from({ length: endHour - startHour + 1 }, (_, i) => {
          const percent = (i / (endHour - startHour)) * 100;
          return (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                left: `${percent}%`,
                top: 0,
                bottom: 0,
                width: '1px',
                bgcolor: 'grey.200',
                zIndex: 0,
              }}
            />
          );
        })}

        {/* Override block */}
        {override && (
          <>
            {overrideBlock ? (
              <TimelineAvailabilityBlock
                leftPercent={overrideBlock.left}
                widthPercent={overrideBlock.width}
                variant="override"
                tooltip={
                  <Box>
                    <Typography variant="caption" fontWeight={600} display="block">
                      {override.name}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {override.date} &middot; {override.formattedTimeRange}
                    </Typography>
                    {isAvailable && blocks.length > 0 && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
                        Base: {blocks.map((b) => b.tooltip).join(', ')}
                      </Typography>
                    )}
                  </Box>
                }
              />
            ) : !override.isAvailable ? (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="caption" fontWeight={600} display="block">
                      {override.name}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {override.date} &middot; Off
                    </Typography>
                    {isAvailable && blocks.length > 0 && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
                        Base: {blocks.map((b) => b.tooltip).join(', ')}
                      </Typography>
                    )}
                  </Box>
                }
                placement="top"
                arrow
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 4,
                    borderRadius: '4px',
                    border: '2px dashed #e68a00',
                    bgcolor: '#fff9f3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#8a5300', fontSize: 11 }}>
                    {override.name} &middot; Off
                  </Typography>
                </Box>
              </Tooltip>
            ) : null}
          </>
        )}

        {/* Regular availability blocks (only when no override, or show faded behind override) */}
        {!override &&
          isAvailable &&
          blocks.map((block, idx) => (
            <TimelineAvailabilityBlock
              key={idx}
              leftPercent={block.left}
              widthPercent={block.width}
              variant="available"
              tooltip={block.tooltip}
            />
          ))}

        {/* Closed state */}
        {!override && effectiveClosed && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" sx={{ color: 'grey.400', fontSize: 11, fontWeight: 500 }}>
              {closedLabel}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TimelineDayRow;
